// Generic
import React, { useState, useRef, useEffect } from 'react'
import moment from 'moment'

// Models
import { Employee } from 'models'

// Components
import PopConfirm from 'antd/es/popconfirm'
import Input from 'antd/es/input'
import Form from 'antd/es/form'
import Content from 'components/content'
import Table, { Tr, Td, Th } from 'components/table'
import AddNewEmployeeModal from './add-employee'
import AddTaxCardModal from './add-taxCard'
import Button from 'components/button'
import Notification from 'antd/es/notification'
import ResetPasswordModal from './reset-password'
import Select from 'antd/es/select'
import { useCurrentUser } from 'context/current-user'

// Styles
import './style.scss'
import { useHistory } from 'react-router'
import { useEmployees } from 'context/employees'

const Employees = () => {
  const {
    data: employees,
    loading: employeesLoading,
    refetch: refetchEmployees,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    uploadTaxCard,
    updatePassword,
    queryParams,
    setQueryParamValues
  } = useEmployees()

  const { currentUser } = useCurrentUser()

  // Query Employees
  const [tableFilter, setTableFilter] = useState<string>('')
  // const {
  //   loading: employeesLoading,
  //   data: employeesData,
  //   refetch: retetchEmployees
  // } = useQuery<{ employees: User[] }>(QUERY_EMPLOYEES, {
  //   variables: { language: 'english', filter: tableFilter },
  // });

  const history = useHistory()

  // State
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState<boolean>(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState<Employee | undefined>()
  const [showTaxCardModal, setShowTaxCardModal] = useState<Employee | undefined>()
  const [form] = Form.useForm<Employee>()
  // const [downloadedTaxCard, setDownloadedTaxCard] = useState<string>('')
  // const [downloadedTaxCardUserId, setDownloadedTaxCardUserId] = useState<string>('')
  // const [loadingTaxCard, setLoadingTaxCard] = useState<boolean>(false)

  // Editable Item
  const [editableItem, _setEditableItem] = useState<Employee | undefined>(undefined)
  const refEditableItem = useRef<Employee | undefined>(undefined)
  const setEditableItem = (user: Employee | undefined) => {
    refEditableItem.current = user
    _setEditableItem(user)
  }

  useEffect(() => {
    setQueryParamValues([{ key: 'roles', value: undefined }])
  }, [])

  const capitalizeFirstLetter = (string: string | null) => {
    if (typeof string === 'string') {
      return string.charAt(0).toUpperCase() + string.slice(1)
    }
    return
  }
  const disableDeleteButton = (item: any) => {
    if (item) {
      if (item._id === currentUser?._id || editableItem) {
        return true
      }
    }
    return false
  }
  // useEffect(() => {
  //   if (customersData?.customers[0]) {
  //     setEditableItem(customersData?.customers[0])
  //   }
  // }, [customersData])

  const handleChange = (value: any) => {
    setTableFilter(value)
  }

  return (
    <>
      {/* Add Customer Modal */}
      <AddNewEmployeeModal
        visible={showAddEmployeeModal}
        onCancel={() => setShowAddEmployeeModal(false)}
        onSave={async (employee) => {
          await createEmployee(employee)
          setShowAddEmployeeModal(false)
        }}
      />
      {/* Reset Password */}
      <ResetPasswordModal
        visible={showResetPasswordModal}
        onCancel={() => {
          setShowResetPasswordModal(undefined)
        }}
        onSave={async (value) => {
          try {
            await updatePassword(value)
            Notification.success({ message: 'Salasana muutettiin' })
            setShowResetPasswordModal(undefined)
          } catch (e) {
            console.log(e)
            Notification.error({ message: JSON.stringify(e) })
            setShowResetPasswordModal(undefined)
          }
        }}
      />
      {/* Add Tax Card */}
      <AddTaxCardModal
        visible={showTaxCardModal}
        onCancel={() => {
          setShowTaxCardModal(undefined)
        }}
        onSave={async (taxCard) => {
          await uploadTaxCard(taxCard)
          setShowTaxCardModal(undefined)
        }}
      />

      {/* Content */}
      <Content
        contentScroll={true}
        contentDisablePadding={true}
        toolbarLeft={
          <h1>
            {global.Translations.get('employees')} {employees && `(${employees?.length})`}
          </h1>
        }
        loading={employeesLoading && global.Translations.get('employees')}
        toolbarTheme={'white-shadow'}
        toolbarRight={
          <Select
            style={{ width: '200px' }}
            value={queryParams.roles ? queryParams.roles[0] : 'all'}
            onChange={(value) => {
              if (value === 'all') {
                setQueryParamValues([{ key: 'roles', value: undefined }])
              } else {
                setQueryParamValues([{ key: 'roles', value: [value] }])
              }
            }}
          >
            <Select.Option value={'all'}>Kaikki</Select.Option>
            <Select.Option value={'Admin'}>Ylläpitäjät</Select.Option>
            <Select.Option value={'Employee'}>Työntekijät</Select.Option>
          </Select>
        }
        fullAbsolute={true}
      >
        <Button
          color="green"
          rounded={true}
          style={{ position: 'fixed', right: '1em', bottom: '1em', zIndex: 1000 }}
          label={<i className="fa-solid fa-plus"></i>}
          onClick={() => {
            setShowAddEmployeeModal(true)
          }}
        />
        {/* Form Wrapper */}
        <Form
          form={form}
          name="basic"
          initialValues={{ remember: true }}
          onFinish={async (value: Employee) => {
            try {
              const validated = new Employee(value)
              setEditableItem(validated)
              if (refEditableItem.current) {
                updateEmployee(refEditableItem.current)
              }
              setEditableItem(undefined)
            } catch (e) {
              Notification.error({ message: JSON.stringify(e) })
            }
          }}
          onFinishFailed={() => {}}
          autoComplete="off"
        >
          {/* Table */}
          <Table
            thead={
              <Tr>
                <Th _id="first_name" style={{ width: '200px' }}>
                  Henkilö
                </Th>
                <Th _id="last_name">Sotu</Th>
                <Th _id="email">Sähköposti</Th>
                <Th _id="phone_number">Puhelinnumero</Th>
                <Th _id="job_title">Työnimike</Th>
                <Th _id="address">Osoite</Th>
                <Th _id="hour_rate">Tuntihinta</Th>
                <Th _id="bank_account">Tilinumero</Th>
                <Th _id="city">Kaupunki</Th>
                <Th _id="tax_card">Verokortti</Th>
                <Th _id="password" style={{ width: '150px' }}>
                  Salasana
                </Th>
                <Th _id="last_seen">Viimeksi paikalla</Th>

                <Th _id="buttons" style={{ width: '200px' }}></Th>
              </Tr>
            }
            tbody={employees?.map((item, index) => {
              if (editableItem && editableItem._id === item._id) {
                // EDITABLE TR
                return (
                  <Tr style={{ background: 'rgba(32, 185, 37, 0.62)' }}>
                    <Td>
                      <div>
                        <Form.Item
                          hidden={true}
                          style={{ marginBottom: '0px' }}
                          initialValue={editableItem._id}
                          name="_id"
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                        <div style={{ display: 'flex' }}>
                          <Form.Item style={{ marginBottom: '0px', marginLeft: '2px' }} initialValue={editableItem.last_name} name="last_name">
                            <Input />
                          </Form.Item>
                          <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem.first_name} name="first_name">
                            <Input />
                          </Form.Item>
                        </div>
                        <Form.Item
                          style={{ marginBottom: '0px' }}
                          initialValue={editableItem.roles}
                          name="roles"
                          rules={[{ required: true }]}
                          normalize={(value: string) => {
                            if (value) {
                              return [value]
                            } else {
                              return []
                            }
                          }}
                        >
                          <Select placeholder="Roolit">
                            <Select.Option value={'Admin'}>Admin</Select.Option>
                            <Select.Option value={'Employee'}>Työntekijä</Select.Option>
                          </Select>
                        </Form.Item>
                      </div>
                    </Td>
                    <Td>
                      <Form.Item style={{ marginBottom: '0px', minWidth: '50px' }} initialValue={editableItem.social_number} name="social_number">
                        <Input />
                      </Form.Item>
                    </Td>
                    <Td>
                      <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem.email} name="email">
                        <Input />
                      </Form.Item>
                    </Td>
                    <Td>
                      <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem?.phone_number} name="phone_number">
                        <Input />
                      </Form.Item>
                    </Td>
                    <Td>
                      <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem?.job_tittle} name="job_tittle">
                        <Input />
                      </Form.Item>
                    </Td>
                    <Td>
                      <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem?.address} name="address">
                        <Input />
                      </Form.Item>
                    </Td>
                    <Td>
                      <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem?.hour_rate} name="hour_rate">
                        <Input />
                      </Form.Item>
                    </Td>
                    <Td>
                      <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem?.bank_account} name="bank_account">
                        <Input />
                      </Form.Item>
                    </Td>
                    <Td>
                      <Form.Item
                        style={{ marginBottom: '0px' }}
                        initialValue={editableItem.city}
                        name="city"
                        rules={[{ required: true }]}
                        normalize={(value: string) => {
                          if (value) {
                            return value
                          } else {
                            return ''
                          }
                        }}
                      >
                        <Select placeholder="Kaupunki">
                          <Select.Option value={'pori'}>Pori</Select.Option>
                          <Select.Option value={'turku'}>Turku</Select.Option>
                          <Select.Option value={'espoo'}>Espoo</Select.Option>
                        </Select>
                      </Form.Item>
                    </Td>
                    <Td>
                      {/* <Form.Item
                          style={{ marginBottom: '0px' }}
                          initialValue={'-'}
                          name="tax_card"
                        >
                          <Button color="primary-inverted" label="Muuta" onClick={() => { }} />
                        </Form.Item> */}
                    </Td>
                    <Td>
                      <Form.Item hidden={true} style={{ marginBottom: '0px' }} initialValue={'Muuta'} name="password">
                        <Input disabled={true} />
                      </Form.Item>
                    </Td>
                    <Td>
                      {/* <Form.Item
                          style={{ marginBottom: '0px' }}
                          initialValue={'-'}
                          name="last_online"
                        >
                          <Input disabled={true} />
                        </Form.Item> */}
                    </Td>
                    <Td>
                      {/* EDITABLE TR BUTTONS */}
                      <Button.ButtonGroup margin="m" style={{} || { position: 'fixed', bottom: '1em', right: '2em' }}>
                        <Button
                          label="Peruuta"
                          icon={<i className="fas fa-history"></i>}
                          onClick={() => {
                            setEditableItem(undefined)
                            form.resetFields()
                          }}
                          color="default"
                        />
                        <Button label="Tallenna" type="submit" icon={<i className="far fa-save"></i>} onClick={() => {}} color="default" />
                      </Button.ButtonGroup>
                    </Td>
                  </Tr>
                )
              } else {
                // STATIC TR
                return (
                  <Tr style={{ borderBottom: '1px solid rgba(5,0,0,0.1)' }}>
                    <Td>
                      <div>
                        <div style={{ fontWeight: 400 }}>
                          {' '}
                          {item.last_name} {item.first_name}
                        </div>
                        <div>
                          {item.roles.map((item: any) => {
                            if (item === 'Admin') {
                              return <span style={{ color: 'rgba(31, 165, 32, 1)' }}>Ylläpitäjä</span>
                            } else {
                              return 'Työntekijä'
                            }
                          })}
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.social_number}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.email}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.phone_number}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.job_tittle}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.address}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.hour_rate ? item.hour_rate + '€' : ''}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.bank_account}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{capitalizeFirstLetter(item.city)}</div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>
                          <Button
                            label={item.tax_card_last_update ? 'Näytä' : 'Lisää'}
                            color={item.tax_card_last_update ? 'green' : 'default-dashed'}
                            onClick={() => {
                              setShowTaxCardModal(item)
                            }}
                          />
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>
                          <Button
                            color="green"
                            label="Vaihda salasana"
                            onClick={() => {
                              setShowResetPasswordModal(item)
                            }}
                          />
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <div>{item.last_seen && moment(item.last_seen).fromNow()}</div>
                      </div>
                    </Td>

                    <Td>
                      {/* STATIC TR BUTTONS */}
                      <Button.ButtonGroup margin="m" style={editableItem ? { opacity: 0.1 } : {}}>
                        <Button
                          icon={<i className="far fa-edit"></i>}
                          onClick={() => {
                            setEditableItem(item)
                            form.resetFields()
                          }}
                          color="default"
                          disabled={editableItem ? true : false}
                        />
                        {/* <Button
                          icon={<i className="fas fa-chart-bar"></i>}
                          onClick={() => {
                            history.push(`/employee-monthly-reports?employeeId=${item._id}`)
                          }}
                          color="default"
                          disabled={editableItem ? true : false}
                        /> */}
                        <PopConfirm
                          title="Haluatko varmasti poistaa tämän käyttäjän？"
                          okText="Kyllä"
                          cancelText="En"
                          onConfirm={() => deleteEmployee(item._id)}
                          disabled={editableItem ? true : false}
                        >
                          <Button
                            icon={<i className="far fa-trash-alt"></i>}
                            onClick={() => {}}
                            color="default"
                            disabled={disableDeleteButton(item)}
                          />
                        </PopConfirm>
                      </Button.ButtonGroup>
                    </Td>
                  </Tr>
                )
              }
            })}
          />
        </Form>
        <div style={{ height: '400px' }}></div>
      </Content>
    </>
  )
}

export default Employees
